import React from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import { FilePlusIcons } from '../../../../../icons/icons';
import { Button, DetailedItem } from '../../../../../ui';
import AsyncCustomSelect from '../../../../../ui/custom_react_select/async_select';
import { CustomSelect } from '../../../../../ui/custom_react_select/custom_select';
import { DatePicker } from '../../../../../ui/input/date_picker/date_picker';
import { EmployeeUser } from '../../../../../utils/types/admin_types';
import {
  JobDescription,
  JobRequisition,
} from '../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { useJobRequisition } from './Hooks';

export const DEFAULT_DOCUMENTS = [
  {
    type: 'Resume / CV',
    description:
      'A standard professional summary of work experience and education.',
    mandatoryFixed: true,
  },
  {
    type: 'Cover Letter',
    description:
      'An introductory letter explaining the candidate\'s motivation.',
  },
  {
    type: 'Academic Transcript',
    description:
      'Official records showing completed courses and grades from educational institutions.',
  },
  {
    type: 'Tertiary Certificates',
    description:
      'Certificates obtained from higher education institutions like universities or colleges.',
  },
  {
    type: 'Professional Certifications',
    description:
      'Industry-specific certificates or professional licenses demonstrating expertise.',
  },
  {
    type: 'Reference Letters',
    description:
      'Letters from former employers or colleagues providing professional recommendations.',
  },
  {
    type: 'Work Portfolio',
    description:
      'Samples of previous projects, case studies, or links to individual repositories.',
  },
];

type JobRequisitionTabProps = {
  jobRequisition: JobRequisition;
  setJobRequisition: React.Dispatch<React.SetStateAction<JobRequisition>>;
  jobDescription?: JobDescription;
  setJobDescription?: React.Dispatch<React.SetStateAction<JobDescription>>;
  employees: EmployeeUser[];
  defaultHiringManager: SelectOptions[] | undefined;
  defaultInternalRecruiter: SelectOptions[] | undefined;
  setDefaultHiringManager: React.Dispatch<
    React.SetStateAction<SelectOptions[] | undefined>
  >;
  setDefaultInternalRecruiter: React.Dispatch<
    React.SetStateAction<SelectOptions[] | undefined>
  >;
  completedElement: () => void;
  editable: boolean;
  companyOptions: SelectOptions[];
  selectedCompanyId: string;
  setSelectedCompanyId: React.Dispatch<React.SetStateAction<string>>;
};

export const JobRequisitionPage: React.FC<JobRequisitionTabProps> = ({
  jobRequisition,
  setJobRequisition,
  jobDescription,
  setJobDescription,
  employees,
  defaultHiringManager,
  defaultInternalRecruiter,
  setDefaultHiringManager,
  setDefaultInternalRecruiter,
  completedElement,
  editable,
  companyOptions,
  selectedCompanyId,
  setSelectedCompanyId,
}) => {
  const { t } = useTranslation(NS.adminPages);

  const selectedCompany =
    companyOptions.find((option) => option.value === selectedCompanyId) ??
    companyOptions[0] ??
    null;

  const jobRequisitionTypes: SelectOptions[] = [
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobRequisition.typeReplacement), value: 'Replacement' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobRequisition.typeNewRole), value: 'New Role' },
  ];

  const applicantTypes: SelectOptions[] = [
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobRequisition.scopeExternal), value: 'External' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobRequisition.scopeInternal), value: 'Internal' },
  ];

  const {
    handleChange,
    handleEmployeeChange,
    date,
    tagInput,
    setTagInput,
    removeTag,
    changeDate,
    loadOptionsDebounced,
    handleTagKeyDown,
    loading,
    defaultEmployeeUserOptions,
    submitJobRequisition,
  } = useJobRequisition({
    jobRequisition,
    setJobRequisition,
    jobDescription,
    setJobDescription,
    employees,
    setDefaultHiringManager,
    setDefaultInternalRecruiter,
    completedElement,
    editable,
    defaultHiringManager,
    defaultInternalRecruiter,
  });

  const K = ADMIN_PAGES_KEYS.jobOpenings.jobRequisition;

  return (
    <div className='relative flex flex-col gap-4 h-full'>
      { !editable && (
        <div className={'bg-alert-warning/20 p-2'}>
          <div className='font-medium'>
            { t(K.notEditableBanner) }{ ' ' }
            <span className='text-gray-700'>
              { t(K.notEditableDetail) }
            </span>
          </div>
        </div>
      ) }
      <div className='grid grid-cols-3 gap-x-8 gap-y-5 overflow-visible'>
        { editable ? (
          <div>
            <label className='block text-slate-600 font-semibold text-[13px] mb-1.5 ml-0.5'>
              { t(K.companyLabel) }
            </label>
            <CustomSelect
                name='company'
                value={selectedCompany}
                options={companyOptions}
                onChange={(option: SelectOptions) => setSelectedCompanyId(option.value)}
                disabled={!editable}
            />
          </div>
        ) : (
          <DetailedItem
              label={t(K.companyLabel)}
              text={selectedCompany?.label ?? ''}
              helpertext={t(K.companyHelperText)}
          />
        ) }
        { editable ? (
          <div>
            <label className='block text-slate-600 font-semibold text-[13px] mb-1.5 ml-0.5'>
              { t(K.typeLabel) }*
            </label>
            <CustomSelect
                name='jobRequisitionType'
                value={{
                  label: jobRequisition.jobRequisitionType,
                  value: jobRequisition.jobRequisitionType,
                }}
                options={jobRequisitionTypes}
                onChange={handleChange}
                disabled={!editable}
            />
          </div>
        ) : (
          <DetailedItem
              label={t(K.typeLabel)}
              text={jobRequisition.jobRequisitionType}
              helpertext={t(K.typeHelperText)}
          />
        ) }
        { editable ? (
          <div>
            <label className='block text-slate-800 font-semibold text-[13px] mb-1.5 ml-0.5'>
              { t(K.deadlineLabel) }*
            </label>
            <DatePicker
                startDate={date.startDate}
                endDate={date.endDate}
                onChange={changeDate}
                asSingle
                useRange={false}
                disabled={!editable}
            />
          </div>
        ) : (
          <DetailedItem
              type='date'
              label={t(K.deadlineLabel)}
              text={jobRequisition.deadline}
              helpertext={t(K.deadlineHelperText)}
          />
        ) }
        { editable ? (
          <div>
            <label className='block text-slate-600 font-semibold text-[13px] mb-1.5 ml-0.5'>
              { t(K.scopeLabel) }*
            </label>
            <CustomSelect
                name='applicantScope'
                value={jobRequisition.applicantScope.map((item) => ({
                  label: item,
                  value: item,
                }))}
                options={applicantTypes}
                onChange={handleChange}
                isMulti
                disabled={!editable}
            />
          </div>
        ) : (
          <DetailedItem
              type='tags'
              label={t(K.scopeLabel)}
              tags={jobRequisition.applicantScope}
              helpertext={t(K.scopeHelperText)}
          />
        ) }

        { editable ? (
          <div>
            <label className='block text-slate-600 font-semibold text-[13px] mb-1.5 ml-0.5'>
              { t(K.hiringManagerLabel) }*
            </label>
            <AsyncCustomSelect
                name='hiringManager'
                value={defaultHiringManager}
                defaultOptions={defaultEmployeeUserOptions}
                onChange={handleEmployeeChange}
                loadOptions={loadOptionsDebounced}
                isMulti
                disabled={!editable}
            />
          </div>
        ) : (
          <DetailedItem
              type='list'
              label={t(K.hiringManagerLabel)}
              list={defaultHiringManager || []}
              helpertext={t(K.hiringManagerHelperText)}
          />
        ) }
        { editable ? (
          <div>
            <label className='block text-slate-600 font-semibold text-[13px] mb-1.5 ml-0.5'>
              { t(K.hrRecruiterLabel) }*
            </label>
            <AsyncCustomSelect
                name='internalRecruiter'
                value={defaultInternalRecruiter}
                defaultOptions={defaultEmployeeUserOptions}
                onChange={handleEmployeeChange}
                loadOptions={(inputValue, callback) =>
                  loadOptionsDebounced(inputValue, callback)
                }
                isMulti
                disabled={!editable}
            />
          </div>
        ) : (
          <DetailedItem
              type='list'
              label={t(K.hrRecruiterLabel)}
              list={defaultInternalRecruiter || []}
              helpertext={t(K.hrRecruiterHelperText)}
          />
        ) }
        { editable ? (
          <div>
            <label className='block text-slate-800 font-semibold text-[13px] mb-1.5 ml-0.5'>
              { t(K.tagsLabel) }{ ' ' }
              <span className='text-slate-500'>{ t(K.tagsOptional) }</span>
            </label>
            <div className='flex flex-wrap gap-1.5 border border-slate-200 bg-white p-1.5 rounded-md focus-within:ring-1 focus-within:ring-emerald-500/20 focus-within:border-emerald-500/40 transition-all duration-200 min-h-[38px]'>
              { jobRequisition.tags?.map((tag, idx) => (
                <span
                    key={idx}
                    className='bg-emerald-50 text-emerald-700 font-semibold text-[11px] px-2 py-0.5 rounded-full border border-emerald-100 flex items-center gap-1'
                >
                  { tag }
                  <button
                      onClick={() => removeTag(idx)}
                      className='hover:text-emerald-900'
                  >
                    &times;
                  </button>
                </span>
              )) }
              <input
                  type='text'
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder={t(K.tagsPlaceholder)}
                  className='flex-1 min-w-[80px] text-[13px] placeholder:text-slate-400 outline-none bg-transparent py-0.5'
                  disabled={!editable}
              />
            </div>
          </div>
        ) : (
          jobRequisition.tags &&
          jobRequisition.tags.length > 0 && (
            <DetailedItem
                type='tags'
                label={t(K.tagsLabel)}
                tags={jobRequisition.tags}
                helpertext={t(K.scopeHelperText)}
            />
          )
        ) }
      </div>

      { editable && (
        <div className='flex w-full justify-end mt-2 pt-4 border-t border-slate-100'>
          <Button
              onClick={submitJobRequisition}
              variant='primary'
              label={t(K.saveAndContinue)}
              disabled={
                loading ||
              jobRequisition.applicantScope.length === 0 ||
              !jobRequisition.deadline ||
              jobRequisition.hiringManager.length === 0 ||
              jobRequisition.internalRecruiter.length === 0 ||
              !editable
              }
          />
        </div>
      ) }
    </div>
  );
};
